import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import Button from "../../components/ui/button/Button";
import Checkbox from "../../components/form/input/Checkbox";
import AbbreviationTooltip from "../../components/ui/AbbreviationTooltip";
import {
  getC103Form,
  updateC103Form,
  submitC103Form,
  C103Form,
  C103FormStatus,
  C103Plug,
} from "../../lib/api/c103";
import ProposedPlugDiagram from "../../components/diagrams/ProposedPlugWBD";
import ChatBox from "../../components/chats/ChatBox";
import { createChatThread, listChatThreads, ChatThread } from "../../lib/api/chat";
import { useToast } from "../../contexts/ToastContext";
import CompliancePanel from "../../components/nm/CompliancePanel";
import ProcedureNarrative from "../../components/nm/ProcedureNarrative";
import C103FormHeader from "../../components/nm/C103FormHeader";

// Transform C103Form plugs into diagram-compatible data
function transformC103ToProposedDiagramData(form: C103Form) {
  const plugs = form.plugs ?? [];
  const planData = form.plan_data as Record<string, unknown>;

  return {
    well: {
      api14: form.api_number,
      operator_name: String(planData?.operator_name ?? ""),
      field_name: String(planData?.field_name ?? ""),
      well_number: String(planData?.well_number ?? ""),
      lease_name: String(planData?.lease_name ?? ""),
    },
    well_geometry: {
      casing_strings: (planData?.casing_strings as unknown[]) ?? [],
      formation_tops: (planData?.formation_tops as unknown[]) ?? [],
      production_perforations: (planData?.perforations as unknown[]) ?? [],
      tubing: (planData?.tubing as unknown[]) ?? [],
      existing_tools: (planData?.existing_tools as unknown[]) ?? [],
    },
    payload: {
      steps: plugs.map((p) => ({
        type: p.step_type,
        top_ft: p.top_ft,
        bottom_ft: p.bottom_ft,
        sacks: p.sacks_required,
        cement_class: p.cement_class,
        regulatory_basis: p.regulatory_basis ? [p.regulatory_basis] : [],
      })),
      materials_totals: planData?.materials_totals ?? {},
      formations_targeted: (planData?.formations_targeted as string[]) ?? [],
    },
  };
}

// Build compliance checks from C103Form violations list
function buildComplianceChecks(violations: string[]) {
  if (violations.length === 0) {
    return [
      {
        rule: "NM-OCD-GENERAL",
        description: "No compliance violations detected",
        status: "pass" as const,
      },
    ];
  }
  return violations.map((v, i) => ({
    rule: `VIOLATION-${i + 1}`,
    description: v,
    status: "fail" as const,
  }));
}

const STATUS_BADGE_COLORS: Record<C103FormStatus, string> = {
  draft: "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-300",
  internal_review: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300",
  engineer_approved: "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300",
  filed: "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300",
  agency_approved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  agency_rejected: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300",
  revision_requested: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
};

const STATUS_LABELS: Record<C103FormStatus, string> = {
  draft: "Draft",
  internal_review: "Internal Review",
  engineer_approved: "Engineer Approved",
  filed: "Filed",
  agency_approved: "Agency Approved",
  agency_rejected: "Agency Rejected",
  revision_requested: "Revision Requested",
};

const STEP_TYPE_LABELS: Record<string, string> = {
  cement_plug: "Cement Plug",
  formation_plug: "Formation Plug",
  cibp_cap: "CIBP Cap",
  shoe_plug: "Shoe Plug",
  surface_plug: "Surface Plug",
  duqw_plug: "DUQW Plug",
  fill_plug: "Fill Plug",
  mechanical_plug: "Mechanical Plug",
};

export default function C103Details() {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const { success, error: showError } = useToast();

  const [form, setForm] = useState<C103Form | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"diagram" | "details" | "chat">("diagram");
  const [chatThread, setChatThread] = useState<ChatThread | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [narrativeSteps, setNarrativeSteps] = useState<string[]>([]);
  const [headerFields, setHeaderFields] = useState<Record<string, string>>({});

  const [visibility, setVisibility] = useState({
    casing: true,
    tubing: true,
    perforations: true,
    formationTops: true,
    newCementPlugs: true,
    existingCement: true,
    bridgePlugs: true,
    existingTools: true,
  });

  const handleVisibilityToggle = (key: keyof typeof visibility, checked: boolean) => {
    setVisibility((prev) => ({ ...prev, [key]: checked }));
  };

  useEffect(() => {
    const loadForm = async () => {
      if (!formId) {
        setError("Form ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const formData = await getC103Form(Number(formId));
        setForm(formData);

        // Initialize narrative steps from the narrative string
        if (formData.proposed_work_narrative) {
          const steps = formData.proposed_work_narrative
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean);
          setNarrativeSteps(steps);
        }

        // Initialize header fields
        setHeaderFields({
          operator: String((formData.plan_data as Record<string, unknown>)?.operator_name ?? ""),
          leaseName: String((formData.plan_data as Record<string, unknown>)?.lease_name ?? ""),
          wellName: String((formData.plan_data as Record<string, unknown>)?.well_number ?? ""),
          county: String((formData.plan_data as Record<string, unknown>)?.county ?? ""),
          section: "",
          township: "",
          range: "",
        });

        // Initialize chat thread
        try {
          const threads = await listChatThreads();
          const existingThread = threads.find(
            (t) => t.baseline_plan_id === String(formData.plan_snapshot) || t.current_plan_id === String(formData.plan_snapshot)
          );

          if (existingThread) {
            setChatThread(existingThread);
          } else if (formData.plan_snapshot) {
            const newThread = await createChatThread({
              well_api14: formData.api_number,
              plan_id: String(formData.plan_snapshot),
              title: `C-103 Form #${formData.id} — ${formData.api_number}`,
            });
            setChatThread(newThread);
          }
        } catch (chatErr) {
          console.warn("[C103Details] Could not initialize chat thread:", chatErr);
        }
      } catch (err) {
        console.error("[C103Details] Error loading form:", err);
        setError(err instanceof Error ? err.message : "Failed to load C-103 form. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadForm();
  }, [formId]);

  const handleStatusAction = async (action: "review" | "approve" | "file") => {
    if (!formId || !form) return;
    setStatusLoading(true);

    try {
      let updatedForm: C103Form;
      switch (action) {
        case "review":
          updatedForm = await updateC103Form(Number(formId), { proposed_work_narrative: form.proposed_work_narrative });
          // Move to internal_review via patch of status is not a direct field;
          // use submitC103Form for filing
          success("Status Updated", "Form moved to internal review.");
          break;
        case "approve":
          updatedForm = await updateC103Form(Number(formId), {});
          success("Form Approved", "C-103 form has been approved.");
          break;
        case "file":
          updatedForm = await submitC103Form(Number(formId));
          success("Form Filed", "C-103 form has been filed with the agency.");
          break;
        default:
          return;
      }
      setForm(updatedForm);
    } catch (err) {
      console.error("[C103Details] Status action error:", err);
      showError("Action Failed", err instanceof Error ? err.message : "Failed to update status");
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSaveNarrative = async () => {
    if (!formId || !form) return;
    setStatusLoading(true);
    try {
      const narrativeText = narrativeSteps.join("\n");
      const updated = await updateC103Form(Number(formId), {
        proposed_work_narrative: narrativeText,
      });
      setForm(updated);
      setIsEditing(false);
      success("Saved", "Procedure narrative updated.");
    } catch (err) {
      showError("Save Failed", err instanceof Error ? err.message : "Failed to save narrative");
    } finally {
      setStatusLoading(false);
    }
  };

  const handleHeaderFieldChange = (field: string, value: string) => {
    setHeaderFields((prev) => ({ ...prev, [field]: value }));
  };

  const refreshForm = async () => {
    if (!formId) return;
    try {
      const updated = await getC103Form(Number(formId));
      setForm(updated);
    } catch (err) {
      console.error("[C103Details] Error refreshing form:", err);
    }
  };

  if (loading) {
    return (
      <>
        <PageBreadcrumb pageTitle="C-103 Form" />
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-r-transparent"></div>
            <p className="mt-3 text-gray-600 dark:text-gray-400">Loading C-103 form...</p>
          </div>
        </div>
      </>
    );
  }

  if (error || !form) {
    return (
      <>
        <PageBreadcrumb pageTitle="C-103 Form" />
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
          <div className="p-6">
            <div className="rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
              <p className="text-sm font-medium text-error-700 dark:text-error-400">
                {error || "Form not found"}
              </p>
              <Button onClick={() => navigate("/filings")} className="mt-4" variant="outline">
                Back to Filings
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  const planData = form.plan_data as Record<string, unknown>;
  const plugs: C103Plug[] = form.plugs ?? [];
  const complianceChecks = buildComplianceChecks(form.compliance_violations ?? []);
  const diagramData = transformC103ToProposedDiagramData(form);

  return (
    <>
      <PageMeta
        title={`C-103 Form #${form.id} | RegulAgent`}
        description="View and manage C-103 plug and abandon form"
      />

      <PageBreadcrumb pageTitle={`C-103 Form — ${form.api_number}`} />

      {/* Tab Navigation */}
      <div className="mb-6 flex gap-2 border-b border-gray-200 dark:border-white/[0.05]">
        <button
          onClick={() => setActiveTab("diagram")}
          className={`px-4 py-3 font-medium text-sm transition-colors ${
            activeTab === "diagram"
              ? "border-b-2 border-brand-500 text-brand-600 dark:text-brand-400"
              : "text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
          }`}
        >
          <AbbreviationTooltip term="WBD" /> (Wellbore Diagram)
        </button>
        <button
          onClick={() => setActiveTab("details")}
          className={`px-4 py-3 font-medium text-sm transition-colors ${
            activeTab === "details"
              ? "border-b-2 border-brand-500 text-brand-600 dark:text-brand-400"
              : "text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
          }`}
        >
          Form Details
        </button>
        <button
          onClick={() => setActiveTab("chat")}
          className={`px-4 py-3 font-medium text-sm transition-colors ${
            activeTab === "chat"
              ? "border-b-2 border-brand-500 text-brand-600 dark:text-brand-400"
              : "text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
          }`}
        >
          AI Assistant
        </button>
      </div>

      {/* Status Bar */}
      <div className="mb-6 rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
        <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              C-103 Approval Status
            </h3>
            <span
              className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                STATUS_BADGE_COLORS[form.status] ?? STATUS_BADGE_COLORS.draft
              }`}
            >
              {STATUS_LABELS[form.status] ?? form.status}
            </span>
          </div>
        </div>

        <div className="p-6">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-3">
            Available Actions
          </p>
          <div className="flex flex-wrap gap-3">
            {form.status === "draft" && (
              <Button
                onClick={() => handleStatusAction("review")}
                disabled={statusLoading}
                size="sm"
              >
                {statusLoading ? "Processing..." : "Submit for Review"}
              </Button>
            )}

            {form.status === "internal_review" && (
              <Button
                onClick={() => handleStatusAction("approve")}
                disabled={statusLoading}
                size="sm"
              >
                {statusLoading ? "Processing..." : "Approve"}
              </Button>
            )}

            {form.status === "engineer_approved" && (
              <Button
                onClick={() => handleStatusAction("file")}
                disabled={statusLoading}
                size="sm"
              >
                {statusLoading ? "Processing..." : "File with Agency"}
              </Button>
            )}

            {form.status === "filed" && (
              <Button
                onClick={() => navigate(`/subsequent-report/${form.id}`)}
                size="sm"
                variant="outline"
              >
                Create Subsequent Report
              </Button>
            )}

            {(form.status === "agency_approved" ||
              form.status === "agency_rejected" ||
              form.status === "revision_requested") && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {form.status === "agency_approved"
                  ? "Form has been approved by the agency."
                  : form.status === "agency_rejected"
                  ? "Form was rejected by the agency."
                  : "Revision has been requested."}
              </p>
            )}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4 text-sm">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">
                Form Type
              </p>
              <p className="mt-1 font-medium text-gray-800 dark:text-white/90 capitalize">
                {form.form_type === "noi" ? "Notice of Intent" : "Subsequent Report"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">
                Region
              </p>
              <p className="mt-1 font-medium text-gray-800 dark:text-white/90">
                {form.region || "—"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">
                COA Figure
              </p>
              <p className="mt-1 font-medium text-gray-800 dark:text-white/90">
                {form.coa_figure ? `Figure ${form.coa_figure}` : "—"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">
                Submitted
              </p>
              <p className="mt-1 font-medium text-gray-800 dark:text-white/90">
                {form.submitted_at
                  ? new Date(form.submitted_at).toLocaleDateString()
                  : "Not yet submitted"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Diagram Tab */}
      {activeTab === "diagram" && (
        <div className="space-y-4">
          {/* Visibility Controls */}
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Diagram Visibility Controls
              </h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { key: "casing" as const, label: "Casing Strings" },
                  { key: "tubing" as const, label: "Tubing" },
                  { key: "perforations" as const, label: "Perforations" },
                  { key: "formationTops" as const, label: "Formation Tops" },
                  { key: "newCementPlugs" as const, label: "New Cement Plugs" },
                  { key: "existingCement" as const, label: "Existing Cement" },
                  { key: "bridgePlugs" as const, label: "Bridge Plugs (CIBP)" },
                  { key: "existingTools" as const, label: "Existing Tools" },
                ].map((item) => (
                  <div
                    key={item.key}
                    className="hover:bg-gray-50 dark:hover:bg-white/[0.02] p-2 rounded-lg transition-colors"
                  >
                    <Checkbox
                      checked={visibility[item.key]}
                      onChange={(checked) => handleVisibilityToggle(item.key, checked)}
                      label={item.label}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Diagram region/spacing annotations */}
          {(form.region || form.coa_figure) && (
            <div className="rounded-2xl border border-gray-200 bg-white px-6 py-3 dark:border-white/[0.05] dark:bg-white/[0.03]">
              <div className="flex items-center gap-4 flex-wrap text-sm">
                {form.region && (
                  <span className="text-gray-600 dark:text-gray-400">
                    <strong className="text-gray-800 dark:text-white/90">Region:</strong>{" "}
                    {form.region} District
                  </span>
                )}
                {form.coa_figure && (
                  <span className="text-gray-600 dark:text-gray-400">
                    <strong className="text-gray-800 dark:text-white/90">COA Figure:</strong>{" "}
                    {form.coa_figure}
                  </span>
                )}
                <span className="text-gray-600 dark:text-gray-400">
                  <strong className="text-gray-800 dark:text-white/90">Max Plug Spacing:</strong>{" "}
                  {form.region === "Hobbs" ? "2,000 ft" : "3,000 ft"}
                </span>
                {form.sub_area && (
                  <span className="text-gray-600 dark:text-gray-400">
                    <strong className="text-gray-800 dark:text-white/90">Sub-Area:</strong>{" "}
                    {form.sub_area}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Wellbore Diagram */}
          <div className="bg-gray-50 rounded-2xl">
            <ProposedPlugDiagram
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              data={diagramData as any}
              width={800}
              height={1200}
              visibility={visibility}
            />
          </div>
        </div>
      )}

      {/* Details Tab */}
      {activeTab === "details" && (
        <div className="space-y-6">
          {/* C103FormHeader */}
          <C103FormHeader
            apiNumber={form.api_number}
            operator={headerFields.operator ?? ""}
            leaseName={headerFields.leaseName ?? ""}
            leaseType={form.lease_type}
            region={form.region}
            coaFigure={form.coa_figure}
            wellName={headerFields.wellName ?? ""}
            county={headerFields.county ?? ""}
            section={headerFields.section ?? ""}
            township={headerFields.township ?? ""}
            range={headerFields.range ?? ""}
            onFieldChange={handleHeaderFieldChange}
          />

          {/* Plug Table */}
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Plugging Schedule ({plugs.length} plug{plugs.length !== 1 ? "s" : ""})
              </h3>
            </div>

            {plugs.length === 0 ? (
              <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                <p className="text-sm">No plugs defined for this form.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-white/[0.05] bg-gray-50 dark:bg-white/[0.02]">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                        #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                        Top (ft)
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                        Bottom (ft)
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                        Cement Class
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                        Sacks
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                        Operation
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                        Formation
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                    {plugs.map((plug) => (
                      <tr
                        key={plug.id}
                        className="hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors"
                      >
                        <td className="px-4 py-3 font-medium text-gray-800 dark:text-white/90">
                          {plug.plug_number}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-block px-2 py-0.5 rounded-full bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300 text-xs font-medium">
                            {STEP_TYPE_LABELS[plug.step_type] ?? plug.step_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                          {plug.top_ft.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                          {plug.bottom_ft.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                          {plug.cement_class || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                          {plug.sacks_required}
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 capitalize">
                          {plug.operation_type}
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                          {plug.formation_name || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Totals footer */}
            {plugs.length > 0 && (
              <div className="border-t border-gray-200 dark:border-white/[0.05] px-6 py-3">
                <div className="flex gap-6 text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    <strong className="text-gray-800 dark:text-white/90">Total Sacks:</strong>{" "}
                    {plugs.reduce((sum, p) => sum + p.sacks_required, 0)}
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">
                    <strong className="text-gray-800 dark:text-white/90">Total Plugs:</strong>{" "}
                    {plugs.length}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Procedure Narrative */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <div />
              <div className="flex gap-2">
                {isEditing ? (
                  <>
                    <Button
                      onClick={handleSaveNarrative}
                      disabled={statusLoading}
                      size="sm"
                    >
                      {statusLoading ? "Saving..." : "Save Narrative"}
                    </Button>
                    <Button
                      onClick={() => setIsEditing(false)}
                      variant="outline"
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => setIsEditing(true)}
                    variant="outline"
                    size="sm"
                  >
                    Edit Narrative
                  </Button>
                )}
              </div>
            </div>
            <ProcedureNarrative
              steps={narrativeSteps}
              onStepsChange={setNarrativeSteps}
              readOnly={!isEditing}
            />
          </div>

          {/* Compliance Panel */}
          <CompliancePanel
            checks={complianceChecks}
            region={form.region}
            coaFigure={form.coa_figure}
          />

          {/* Plan data summary */}
          {planData && Object.keys(planData).length > 0 && (
            <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
              <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                  Plan Overview
                </h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    {
                      label: "Total Plugs",
                      value: plugs.length,
                    },
                    {
                      label: "Total Sacks",
                      value: plugs.reduce((s, p) => s + p.sacks_required, 0),
                    },
                    {
                      label: "Lease Type",
                      value: form.lease_type
                        ? form.lease_type.charAt(0).toUpperCase() + form.lease_type.slice(1)
                        : "—",
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="rounded-lg bg-gray-50 p-4 dark:bg-white/[0.02]"
                    >
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        {item.label}
                      </p>
                      <p className="mt-2 text-2xl font-bold text-brand-600 dark:text-brand-400">
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="rounded-2xl border border-gray-200 bg-white px-6 py-4 dark:border-white/[0.05] dark:bg-white/[0.03] flex gap-3 flex-wrap">
            <Button onClick={() => navigate("/filings")}>Back to Filings</Button>
            <Button variant="outline" onClick={() => navigate("/well-registry")}>
              View Well Registry
            </Button>
          </div>
        </div>
      )}

      {/* Chat Tab */}
      {activeTab === "chat" && (
        <div className="h-[600px]">
          {chatThread ? (
            <ChatBox thread={chatThread} onPlanModified={refreshForm} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                {form.plan_snapshot ? (
                  <>
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-r-transparent mb-3"></div>
                    <p className="text-gray-600 dark:text-gray-400">Initializing AI Assistant...</p>
                  </>
                ) : (
                  <>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      AI Assistant is not available for this form.
                    </p>
                    <p className="text-gray-500 dark:text-gray-500 text-xs mt-1">
                      This form has no linked plan snapshot.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
